import { useFormik } from "formik";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import Button from "src/components/buttons/Button";
import Dropdown from "src/components/dropdowns/Dropdown";
import UploadImage from "src/components/image-upload/UploadImage";
import Input from "src/components/inputs/Input";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import { CATEGORY, PRODUCT_STATUS_OPTIONS, UNIT } from "src/utils/constants";
import { number, object, string } from "yup";
import {
  useCreateProduct,
  useGetProductData,
  useUpdateProduct,
} from "../api-products";

const FORM_FIELDS = {
  NAME: "name",
  DESCRIPTION: "description",
  STOCK: "stock",
  UNIT: "unit",
  PRICE: "price",
  STATUS: "status",
  CATEGORY: "category",
  IMAGE: "image",
};

const CreateProduct = ({
  isInViewMode = false,
  onSuccess,
  label = "",
  tabName,
}: {
  isInViewMode?: boolean;
  onSuccess?: () => void;
  label?: string;
  tabName?: string;
}) => {
  useSetActiveTab(tabName ?? TAB_NAMES.PRODUCTS);
  const navigate = useNavigate();
  const { productId: id } = useParams();

  const productId = useMemo(() => Number(id), [id]);

  const isInEditMode = useMemo(() => !!productId, [productId]);

  const { errorNotification, successNotification } = useNotification();

  const { isLoading: isGetProductLoading, data } = useGetProductData(productId);

  const productData = useMemo(() => data?.data ?? {}, [data?.data]);

  const {
    isLoading: isUpdateLoading,
    execute: updateProduct,
    isSuccess: isUpdateSuccess,
    error: isUpdateError,
  } = useUpdateProduct(productId);

  const { isLoading, isSuccess, execute, error } = useCreateProduct();

  const { values, errors, handleSubmit, setFieldValue, touched } = useFormik({
    enableReinitialize: true,
    initialValues: {
      [FORM_FIELDS.NAME]: productData?.[FORM_FIELDS.NAME] ?? "",
      [FORM_FIELDS.DESCRIPTION]: productData?.[FORM_FIELDS.DESCRIPTION] ?? "",
      [FORM_FIELDS.PRICE]: productData?.[FORM_FIELDS.PRICE] ?? "",
      [FORM_FIELDS.UNIT]: productData?.[FORM_FIELDS.UNIT] ?? "",
      [FORM_FIELDS.STOCK]: productData?.[FORM_FIELDS.STOCK] ?? "",
      [FORM_FIELDS.CATEGORY]: productData?.[FORM_FIELDS.CATEGORY] ?? "",
      [FORM_FIELDS.STATUS]: productData?.[FORM_FIELDS.STATUS] ?? "",
      [FORM_FIELDS.IMAGE]: productData?.[FORM_FIELDS.IMAGE] ?? "",
    },
    validationSchema: object({
      [FORM_FIELDS.NAME]: string().required("This is a Required field."),
      [FORM_FIELDS.DESCRIPTION]: string().required("This is a Required field."),
      [FORM_FIELDS.PRICE]: number().required("This is a Required field."),
      [FORM_FIELDS.UNIT]: string().required("This is a Required field."),
      [FORM_FIELDS.STOCK]: number().required("This is a Required field."),
      [FORM_FIELDS.CATEGORY]: string().required("This is a Required field."),
      [FORM_FIELDS.STATUS]: string().required("This is a Required field."),
    }),
    onSubmit: (data) => {
      if (isInEditMode) {
        updateProduct(data);
        return;
      }
      execute(data);
    },
  });

  useEffect(() => {
    if (isSuccess || isUpdateSuccess) {
      successNotification(
        isUpdateSuccess ? "Product Updated" : "Product Created."
      );
      onSuccess ? onSuccess() : navigate("/vendor/products");
    }
  }, [isSuccess, isUpdateSuccess, navigate, onSuccess, successNotification]);

  useEffect(() => {
    if (error || isUpdateError) {
      errorNotification();
    }
  }, [error, errorNotification, isUpdateError]);

  const isInLoading = useMemo(
    () => isLoading || isGetProductLoading || isUpdateLoading,
    [isGetProductLoading, isLoading, isUpdateLoading]
  );

  const isFieldDisabled = useMemo(
    () => isInViewMode || isInLoading,
    [isInLoading, isInViewMode]
  );

  const getFieldError = useCallback(
    (fieldName: string) => {
      return touched[fieldName] ? (errors[fieldName] as string) : undefined;
    },
    [errors, touched]
  );

  return (
    <div className="flex flex-col gap-[20px] h-full">
      <TabHeader label={label} showBreadcrumb />
      <div className="flex flex-col justify-between gap-6 bg-white rounded-md py-[30px] px-[42px] h-fit">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[30px]">
          <Input
            label="Product Name"
            placeholder="Product Name"
            name={FORM_FIELDS.NAME}
            value={values[FORM_FIELDS.NAME] as string}
            error={getFieldError(FORM_FIELDS.NAME)}
            onChange={(e) => setFieldValue(FORM_FIELDS.NAME, e.target.value)}
            required
            disabled={isFieldDisabled}
          />
          <Input
            type="text"
            label="Description"
            placeholder="Description"
            name={FORM_FIELDS.DESCRIPTION}
            value={values[FORM_FIELDS.DESCRIPTION] as string}
            error={getFieldError(FORM_FIELDS.DESCRIPTION)}
            onChange={(e) =>
              setFieldValue(FORM_FIELDS.DESCRIPTION, e.target.value)
            }
            disabled={isFieldDisabled}
            required
          />
          <Input
            type="text"
            label="Price (₹)"
            placeholder="Price"
            name={FORM_FIELDS.PRICE}
            value={values[FORM_FIELDS.PRICE] as string}
            error={getFieldError(FORM_FIELDS.PRICE)}
            onChange={(e) => setFieldValue(FORM_FIELDS.PRICE, e.target.value)}
            disabled={isFieldDisabled}
            required
          />
          <Dropdown
            label="Unit"
            placeholder="Select Unit"
            className={"w-full"}
            options={UNIT}
            value={values[FORM_FIELDS.UNIT] as string}
            error={getFieldError(FORM_FIELDS.UNIT)}
            onChange={(val) => setFieldValue(FORM_FIELDS.UNIT, val)}
            disabled={isFieldDisabled}
            required
          />
          <Input
            label="Stock Quantity"
            placeholder="Stock Quantity"
            name={FORM_FIELDS.STOCK}
            value={values[FORM_FIELDS.STOCK] as string}
            error={getFieldError(FORM_FIELDS.STOCK)}
            onChange={(e) => setFieldValue(FORM_FIELDS.STOCK, e.target.value)}
            disabled={isFieldDisabled}
            required
          />
          <Dropdown
            label="Category"
            placeholder="Select Category"
            className={"w-full"}
            options={CATEGORY}
            value={values[FORM_FIELDS.CATEGORY] as string}
            error={getFieldError(FORM_FIELDS.CATEGORY)}
            onChange={(val) => setFieldValue(FORM_FIELDS.CATEGORY, val)}
            disabled={isFieldDisabled}
            required
          />
          <Dropdown
            label="Product Status"
            placeholder="Product Status"
            className={"w-full"}
            options={PRODUCT_STATUS_OPTIONS}
            value={values[FORM_FIELDS.STATUS] as string}
            error={getFieldError(FORM_FIELDS.STATUS)}
            onChange={(val) => setFieldValue(FORM_FIELDS.STATUS, val)}
            disabled={isFieldDisabled}
            required
          />
          <UploadImage
            name={FORM_FIELDS.IMAGE}
            label=""
            value={values[FORM_FIELDS.IMAGE]}
            onChange={(file) => setFieldValue(FORM_FIELDS.IMAGE, file)}
            error={getFieldError(FORM_FIELDS.IMAGE)}
            onClear={() => {
              setFieldValue(FORM_FIELDS.IMAGE, null);
            }}
            canDelete={!isInViewMode}
          />
        </div>
        <Button
          text={isLoading ? "Loading..." : "Submit"}
          onClick={handleSubmit}
          primary
          className="h-[40px] px-10"
          isDisabled={isFieldDisabled}
        />
      </div>
    </div>
  );
};

export default CreateProduct;
